import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../API/axiosNeatex';
import type { APIResponse } from '@store/Shared/shared';

interface ProjectDetails {
  id: string;

  operatingUnitId: string;
  ownerId: string;
  sponsorId: string;
  projectType: string;
  contractNo: string;
  location: string;
  baselineStart: string;
  baselineEnd: string;
  plannedStart: string;
  plannedEnd: string;
  deliveryModel: string;
  category: string;
  notes: string;
  name: string;
  name2: string;
  locationId: string;
  code: string;
}

interface Status {
  id: string;
  code: string;
  name: string;
  name2: string;
  description: string;
  isActive: boolean;
  scope: string;
}

export const addProjectDetails = createAsyncThunk(
  'Project/addProjectDetails',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<string>>('Project/AddProjectDetails', item);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProjectDetails = createAsyncThunk(
  'Project/updateProjectDetails',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Project/UpdateProjectDetails', item);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProjectDetails = createAsyncThunk(
  'Project/deleteProjectDetails',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Project/DeleteProjectDetails', { id });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getProjectDetailsById = createAsyncThunk(
  'Project/getProjectDetailsById',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Project/GetProjectDetailsById?id=${id}`);
      if (data.succeeded === true) {
        return data.data;
      } else {
        return null;
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getProjectDetailsList = createAsyncThunk(
  'Project/getProjectDetailsList',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Project/GetProjectDetailsList`);
      if (data.succeeded === true) {
        return data.data;
      } else {
        return [];
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getStatusByScope = createAsyncThunk('Project/getStatusByScope', async (_, { rejectWithValue }) => {
  try {
    const { data } = await ntxAPI.get(`Project/GetStatusByScope`);
    if (data.succeeded === true) {
      return data.data;
    } else {
      return [];
    }
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

const initialState: {
  projectList: ProjectDetails[];
  previousProjectList: ProjectDetails[];
  loading: boolean;
  error: any | null;
  //
  projectById: ProjectDetails | null;
  loading2: boolean;
  error2: any | null;
  //
  StatusList: Status[];
  loading3: boolean;
  error3: any | null;
} = {
  projectList: [],
  previousProjectList: [],
  loading: false,
  error: null,
  //
  projectById: null,
  loading2: false,
  error2: null,
  //
  StatusList: [],
  loading3: false,
  error3: null
};

const projectSlice = createSlice({
  name: 'Project',
  initialState,
  reducers: {
    updateProjectInList: (state, action: PayloadAction<{ projectId: string; updates: Partial<ProjectDetails> }>) => {
      const { projectId, updates } = action.payload;
      const idx = state.projectList.findIndex(p => String(p.id) === String(projectId));
      if (idx !== -1) {
        state.projectList[idx] = { ...state.projectList[idx], ...updates };
      }
    },
    rollbackProjectList: state => {
      state.projectList = state.previousProjectList;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getProjectDetailsList.pending, state => {
        state.loading = true;
      })
      .addCase(getProjectDetailsList.fulfilled, (state, action: PayloadAction<ProjectDetails[]>) => {
        state.loading = false;
        state.projectList = action.payload;
        state.previousProjectList = action.payload;
      })
      .addCase(getProjectDetailsList.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getProjectDetailsById.pending, state => {
        state.loading2 = true;
        state.error2 = null;
      })
      .addCase(getProjectDetailsById.fulfilled, (state, action: PayloadAction<ProjectDetails>) => {
        state.loading2 = false;
        state.projectById = action.payload;
      })
      .addCase(getProjectDetailsById.rejected, (state, action: PayloadAction<any>) => {
        state.loading2 = false;
        state.error2 = action.payload;
      })
      //
      .addCase(getStatusByScope.pending, state => {
        state.loading3 = true;
        state.error3 = null;
      })
      .addCase(getStatusByScope.fulfilled, (state, action: PayloadAction<Status[]>) => {
        state.loading3 = false;
        state.StatusList = action.payload;
      })
      .addCase(getStatusByScope.rejected, (state, action: PayloadAction<any>) => {
        state.loading3 = false;
        state.error3 = action.payload;
      });
    //
  }
});

export const { updateProjectInList, rollbackProjectList } = projectSlice.actions;
export default projectSlice.reducer;
